import React, { useState, useEffect, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, PageHeader, StatCard, StatusPill, fpDataGridSx, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

const DENOMS = [2000, 1000, 500, 200, 100, 50, 25, 20, 10, 5, 1];
const CANT_INICIAL = DENOMS.reduce((a, d) => ({ ...a, ["c" + d]: "" }), {});
const FORM_INICIAL = {
  caja: "01",
  caja_nombre: "CAJA GENERAL",
  localidad: "",
  moneda: "RD",
  turno: "1",
  fecha: new Date().toISOString().split("T")[0],
};

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};
const fmt = (v) =>
  num(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};
const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function openPrintWindow(html) {
  const w = window.open("", "_blank", "width=800,height=600");
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  w.focus();
  w.print();
  return true;
}

// Construye el HTML del cuadre a partir de un objeto normalizado (lo usan el
// formulario nuevo y el detalle del historial).
function buildCuadreHtml(src, detalle) {
  const filasBilletes = src.billetes
    .map((b) => `<tr><td style="text-align:center">${b.cantidad}</td><td style="text-align:right">${Number(b.billete).toLocaleString()}</td><td style="text-align:right">${fmt(b.total)}</td></tr>`)
    .join("");
  const filasMonedas = (src.monedas || [])
    .map((m) => `<tr><td>${m.moneda}</td><td style="text-align:right">${fmt(m.total)}</td><td style="text-align:right">${fmt(m.tasa)}</td><td style="text-align:right">${fmt(m.equiv)}</td></tr>`)
    .join("");
  return `<html><head><title>Cuadre de Caja</title>
    <style>body{font-family:Arial,sans-serif;font-size:12px;color:#0F172A;padding:24px}
    h2{margin:0 0 4px}h4{margin:16px 0 6px;color:#5A6B85}
    table{border-collapse:collapse;width:100%;margin-bottom:8px}
    th,td{border:1px solid #ddd;padding:4px 8px}th{background:#EEF2F8;text-align:left}
    .tot{display:flex;justify-content:space-between;padding:2px 0;max-width:340px}
    .tot b{font-variant-numeric:tabular-nums}</style></head><body>
    <h2>${src.negocio}</h2>
    <div>Caja: ${src.caja} - ${src.caja_nombre} &nbsp; Usuario: ${src.usuario} &nbsp; Turno: ${src.turno} &nbsp; Fecha: ${src.fechaDisplay}</div>
    <h4>Desglose de efectivo (${src.moneda})</h4>
    <table><thead><tr><th style="text-align:center">Cantidad</th><th style="text-align:right">Billete</th><th style="text-align:right">Total</th></tr></thead>
    <tbody>${filasBilletes || '<tr><td colspan="3" style="text-align:center">—</td></tr>'}</tbody></table>
    ${detalle ? `
    <h4>Métodos de pago</h4>
    <div class="tot"><span>Efectivo</span><b>${fmt(src.efectivo)}</b></div>
    <div class="tot"><span>Tarjetas</span><b>${fmt(src.tarjetas)}</b></div>
    <div class="tot"><span>Cheques</span><b>${fmt(src.cheques)}</b></div>
    <div class="tot"><span>Transferencias</span><b>${fmt(src.transferencias)}</b></div>
    <div class="tot"><span>Otros</span><b>${fmt(src.otros)}</b></div>
    ${filasMonedas ? `<h4>Otras monedas</h4>
    <table><thead><tr><th>Moneda</th><th style="text-align:right">Total</th><th style="text-align:right">Tasa</th><th style="text-align:right">Equiv. RD</th></tr></thead>
    <tbody>${filasMonedas}</tbody></table>` : ""}` : ""}
    <h4>Totales</h4>
    <div class="tot"><span>Efectivo</span><b>${fmt(src.efectivo)}</b></div>
    <div class="tot"><span>Total Digitado</span><b>${fmt(src.totalDigitado)}</b></div>
    <div class="tot"><span><b>Total General (RD)</b></span><b>${fmt(src.totalGeneral)}</b></div>
    ${src.problema ? '<p style="color:#E11D48"><b>⚠ Problema con el cuadre de caja</b></p>' : ""}
    </body></html>`;
}

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box sx={{ width: 32, height: 32, borderRadius: 1.4, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: `${color}14`, color }}>
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: "0.74rem", letterSpacing: "0.1em", textTransform: "uppercase", color: FP.color.textSecondary }}>
        {children}
      </Typography>
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", py: 6, color: FP.color.textMuted }}>
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>Sin cuadres</Typography>
      <Typography sx={{ fontSize: "0.82rem" }}>Crea uno nuevo o ajusta los filtros</Typography>
    </Stack>
  );
}

export default function CuadreCaja() {
  const { showAlert } = useGlobalMDAlert();
  const [controller] = useMaterialUIController();
  const { usuarioLog, nombreNegocio } = controller;

  const { post } = useApi();

  // ---- Historial / filtros ----
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroProblema, setFiltroProblema] = useState("");
  const [fecha1, setFecha1] = useState("");
  const [fecha2, setFecha2] = useState("");

  const cargar = useCallback(
    async (ov = {}) => {
      setLoading(true);
      try {
        const { ok, data } = await post("/api/cuadrecaja_listar", {
          problema: ov.problema ?? filtroProblema,
          fecha1: ov.fecha1 ?? fecha1,
          fecha2: ov.fecha2 ?? fecha2,
        });
        if (ok && Array.isArray(data.cuadres)) setRows(data.cuadres);
        else showAlert({ message: data.Error || "No se pudo cargar el historial", color: "error" });
      } catch (e) {
        showAlert({ message: "Error de red o servidor", color: "error" });
      } finally {
        setLoading(false);
      }
    },
    [post, filtroProblema, fecha1, fecha2, showAlert]
  );

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kpis = useMemo(() => {
    let problemas = 0, efectivo = 0, general = 0;
    rows.forEach((r) => {
      if (String(r.problema || "").toUpperCase() === "S") problemas += 1;
      efectivo += num(r.efectivo);
      general += num(r.total_general);
    });
    return { total: rows.length, problemas, efectivo, general };
  }, [rows]);

  // ---- Formulario nuevo cuadre ----
  const [crearOpen, setCrearOpen] = useState(false);
  const [form, setForm] = useState(FORM_INICIAL);
  const [cant, setCant] = useState(CANT_INICIAL);
  const [tarjetas, setTarjetas] = useState("");
  const [cheques, setCheques] = useState("");
  const [transferencias, setTransferencias] = useState("");
  const [otros, setOtros] = useState("");
  const [monedas, setMonedas] = useState([]);
  const [problema, setProblema] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const setFormF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setCantF = (col) => (e) => setCant((p) => ({ ...p, [col]: e.target.value }));

  const efectivo = useMemo(() => DENOMS.reduce((a, d) => a + num(cant["c" + d]) * d, 0), [cant]);
  const totalDigitado = useMemo(
    () => efectivo + num(tarjetas) + num(cheques) + num(transferencias) + num(otros),
    [efectivo, tarjetas, cheques, transferencias, otros]
  );
  const sumaConvertido = useMemo(
    () => monedas.reduce((a, m) => a + num(m.total) * num(m.tasa), 0),
    [monedas]
  );
  const totalGeneral = totalDigitado + sumaConvertido;

  const abrirCrear = () => {
    setForm({ ...FORM_INICIAL, fecha: new Date().toISOString().split("T")[0] });
    setCant(CANT_INICIAL);
    setTarjetas(""); setCheques(""); setTransferencias(""); setOtros("");
    setMonedas([]);
    setProblema(false);
    setCrearOpen(true);
  };
  const limpiar = () => {
    setCant(CANT_INICIAL);
    setTarjetas(""); setCheques(""); setTransferencias(""); setOtros("");
    setMonedas([]);
    setProblema(false);
  };

  const addMoneda = () => setMonedas((p) => [...p, { moneda: "", total: "", tasa: "" }]);
  const removeMoneda = (i) => setMonedas((p) => p.filter((_, idx) => idx !== i));
  const setMonedaF = (i, field) => (e) =>
    setMonedas((p) => p.map((m, idx) => (idx === i ? { ...m, [field]: e.target.value } : m)));

  const guardar = async () => {
    if (efectivo === 0 && totalDigitado === 0 && monedas.length === 0) {
      showAlert({ message: "El cuadre está vacío", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const payload = {
        ...form,
        ...DENOMS.reduce((a, d) => ({ ...a, ["c" + d]: num(cant["c" + d]) }), {}),
        tarjetas: num(tarjetas), cheques: num(cheques),
        transferencias: num(transferencias), otros: num(otros),
        problema: problema ? "S" : "N",
        monedas: monedas
          .filter((m) => (m.moneda || "").trim())
          .map((m) => ({ moneda: m.moneda.trim(), total: num(m.total), tasa: num(m.tasa) })),
      };
      const { ok, data } = await post("/api/cuadrecaja_crear", payload);
      if (ok) {
        showAlert({ message: data.mensajeok || "Cuadre guardado", color: "success" });
        setCrearOpen(false);
        cargar();
        if (data.id) verDetalle(data.id); // abre el detalle para imprimir
      } else showAlert({ message: data.Error || "Error", color: "error" });
    } catch (e) {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardando(false);
    }
  };

  const negocio = (nombreNegocio && nombreNegocio[0] && nombreNegocio[0].ncomp) || "Cuadre de Caja Desglosado";

  // ---- Detalle (historial) ----
  const [detOpen, setDetOpen] = useState(false);
  const [detData, setDetData] = useState(null);
  const [detLoading, setDetLoading] = useState(false);

  const verDetalle = async (id) => {
    setDetData(null);
    setDetLoading(true);
    setDetOpen(true);
    try {
      const { ok, data } = await post("/api/cuadrecaja_detalle", { id });
      if (ok && data.cuadre) setDetData(data.cuadre);
      else {
        showAlert({ message: data.Error || "No se pudo cargar el detalle", color: "error" });
        setDetOpen(false);
      }
    } catch (e) {
      showAlert({ message: "Error de red o servidor", color: "error" });
      setDetOpen(false);
    } finally {
      setDetLoading(false);
    }
  };

  const imprimirDet = (detalle) => {
    const d = detData;
    if (!d) return;
    const src = {
      negocio,
      fechaDisplay: String(d.fecha || "").split("-").reverse().join("/"),
      turno: d.turno, caja: d.caja, caja_nombre: d.caja_nombre,
      usuario: d.usuario || "", moneda: d.moneda,
      billetes: DENOMS.filter((de) => num(d["c" + de]) > 0).map((de) => ({ cantidad: num(d["c" + de]), billete: de, total: num(d["c" + de]) * de })),
      efectivo: num(d.efectivo), tarjetas: num(d.tarjetas), cheques: num(d.cheques), transferencias: num(d.transferencias), otros: num(d.otros),
      monedas: (d.monedas || []).map((m) => ({ moneda: m.moneda, total: num(m.total), tasa: num(m.tasa), equiv: num(m.total_convertido) })),
      totalDigitado: num(d.total_digitado), totalGeneral: num(d.total_general),
      problema: String(d.problema).toUpperCase() === "S",
    };
    if (!openPrintWindow(buildCuadreHtml(src, detalle)))
      showAlert({ message: "Permite las ventanas emergentes para imprimir", color: "warning" });
  };

  const columns = useMemo(
    () =>
      [
        { field: "nocuadre", headerName: "Cuadre #", width: 100,
          renderCell: (p) => <Typography sx={{ fontWeight: 800, color: FP.color.navy }}>{p.value || "—"}</Typography> },
        { field: "fecha", headerName: "Fecha", width: 120 },
        { field: "caja_nombre", headerName: "Caja", width: 150 },
        { field: "usuario", headerName: "Usuario", width: 130 },
        { field: "turno", headerName: "Turno", width: 80 },
        { field: "efectivo", headerName: "Efectivo", width: 140, renderCell: (p) => `RD$ ${fmt(p.value)}` },
        { field: "total_digitado", headerName: "Total Digitado", width: 150, renderCell: (p) => `RD$ ${fmt(p.value)}` },
        { field: "total_general", headerName: "Total General", width: 150,
          renderCell: (p) => <Typography sx={{ fontWeight: 700 }}>{`RD$ ${fmt(p.value)}`}</Typography> },
        { field: "problema", headerName: "Estado", width: 120,
          renderCell: (p) =>
            String(p.value).toUpperCase() === "S"
              ? <StatusPill label="Problema" variant="danger" />
              : <StatusPill label="OK" variant="success" /> },
        { field: "acciones", headerName: "Detalle", width: 110, sortable: false, filterable: false,
          renderCell: (p) => (
            <Button size="small" startIcon={<VisibilityOutlinedIcon />} sx={btnOutline(FP.color.navy)} onClick={() => verDetalle(p.row.id)}>
              Ver
            </Button>
          ) },
      ].map((c) => ({ ...c, align: "center", headerAlign: "center" })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cuadre de Caja Desglosado"
        subtitle="Conteo de efectivo por denominación, métodos de pago y monedas"
        cta={
          <Button startIcon={<AddIcon />} onClick={abrirCrear} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
            Nuevo cuadre
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}><StatCard label="Cuadres" value={kpis.total} icon={PointOfSaleOutlinedIcon} accent={FP.color.navy} /></Grid>
          <Grid item xs={6} md={3}><StatCard label="Con problema" value={kpis.problemas} icon={WarningAmberOutlinedIcon} accent={FP.color.danger} /></Grid>
          <Grid item xs={6} md={3}><StatCard label="Efectivo (RD)" value={`RD$ ${fmt(kpis.efectivo)}`} icon={PaymentsOutlinedIcon} accent={FP.color.success} /></Grid>
          <Grid item xs={6} md={3}><StatCard label="Total General (RD)" value={`RD$ ${fmt(kpis.general)}`} icon={CurrencyExchangeOutlinedIcon} accent={FP.color.info} /></Grid>
        </Grid>

        <Card elevation={0} sx={{ mb: 3, borderRadius: 2.5, border: `1px solid ${FP.color.border}`, boxShadow: FP.shadow.soft }}>
          <CardContent sx={{ p: { xs: 2, md: 2.6 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField select fullWidth label="Estado" value={filtroProblema} onChange={(e) => setFiltroProblema(e.target.value)} sx={fpInputSx}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="N">OK</MenuItem>
                  <MenuItem value="S">Con problema</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth type="date" label="Desde" InputLabelProps={{ shrink: true }} value={fecha1} onChange={(e) => setFecha1(e.target.value)} sx={fpInputSx} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={fecha2} onChange={(e) => setFecha2(e.target.value)} sx={fpInputSx} />
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack direction="row" spacing={1}>
                  <Button fullWidth startIcon={<SearchIcon />} onClick={() => cargar()} disabled={loading} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>Buscar</Button>
                  <Tooltip title="Limpiar filtros">
                    <Button onClick={() => { setFiltroProblema(""); setFecha1(""); setFecha2(""); cargar({ problema: "", fecha1: "", fecha2: "" }); }} sx={{ ...btnOutline(FP.color.navy), minWidth: 44, px: 0 }}>
                      <RestartAltIcon />
                    </Button>
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ borderRadius: 2.5, border: `1px solid ${FP.color.border}`, boxShadow: FP.shadow.soft, overflow: "hidden" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.8, borderBottom: `1px solid ${FP.color.border}` }}>
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>Historial de cuadres</Typography>
            <Chip size="small" label={`${rows.length} ${rows.length === 1 ? "cuadre" : "cuadres"}`} sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }} />
          </Stack>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              getRowId={(r) => r.id}
              loading={loading}
              rowHeight={52}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "260px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Card>
      </Box>

      {/* Dialog Nuevo cuadre */}
      <Dialog open={crearOpen} onClose={() => setCrearOpen(false)} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3, width: "100%", maxWidth: 1180 } }}>
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <PointOfSaleOutlinedIcon sx={{ color: FP.color.navy }} />
          Nuevo Cuadre de Caja
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: FP.color.surface, p: { xs: 2, sm: 3 } }}>
          {/* Cabecera */}
          <Box sx={sectionBox}>
            <SectionTitle icon={PointOfSaleOutlinedIcon} color={FP.color.navy}>Cabecera</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}><TextField fullWidth label="Usuario" value={usuarioLog || ""} InputProps={{ readOnly: true }} sx={fpInputSx} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth label="Turno" value={form.turno} onChange={setFormF("turno")} sx={fpInputSx} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth label="Fecha" value={form.fecha.split("-").reverse().join("/")} InputProps={{ readOnly: true }} sx={fpInputSx} /></Grid>
            </Grid>
          </Box>

          <Grid container spacing={2.4}>
            {/* Desglose */}
            <Grid item xs={12} md={5}>
              <Box sx={{ ...sectionBox, mb: { xs: 2.4, md: 0 } }}>
                <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>Desglose de efectivo</SectionTitle>
                <Stack direction="row" sx={{ px: 0.5, mb: 1 }}>
                  <Typography sx={{ flex: 1, fontSize: "0.7rem", fontWeight: 700, color: FP.color.textMuted, textTransform: "uppercase" }}>Cantidad</Typography>
                  <Typography sx={{ width: 90, textAlign: "right", fontSize: "0.7rem", fontWeight: 700, color: FP.color.textMuted, textTransform: "uppercase" }}>Billete</Typography>
                  <Typography sx={{ width: 110, textAlign: "right", fontSize: "0.7rem", fontWeight: 700, color: FP.color.textMuted, textTransform: "uppercase" }}>Total</Typography>
                </Stack>
                {DENOMS.map((d) => (
                  <Stack key={d} direction="row" alignItems="center" sx={{ mb: 0.8 }}>
                    <Box sx={{ flex: 1, pr: 1 }}>
                      <TextField fullWidth size="small" type="number" value={cant["c" + d]} onChange={setCantF("c" + d)} sx={fpInputSx} inputProps={{ min: 0, style: { textAlign: "center" } }} />
                    </Box>
                    <Typography sx={{ width: 90, textAlign: "right", fontWeight: 700, color: FP.color.textSecondary }}>{d.toLocaleString()}</Typography>
                    <Typography sx={{ width: 110, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt(num(cant["c" + d]) * d)}</Typography>
                  </Stack>
                ))}
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontWeight: 800 }}>Efectivo</Typography>
                  <Typography sx={{ fontWeight: 800, color: FP.color.success }}>RD$ {fmt(efectivo)}</Typography>
                </Stack>
              </Box>
            </Grid>

            {/* Métodos + monedas + totales */}
            <Grid item xs={12} md={7}>
              <Box sx={sectionBox}>
                <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.accentBlue}>Métodos de pago</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Efectivo" value={fmt(efectivo)} InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start">RD$</InputAdornment> }} sx={fpInputSx} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Tarjetas" value={tarjetas} onChange={(e) => setTarjetas(e.target.value)} sx={fpInputSx} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Cheques" value={cheques} onChange={(e) => setCheques(e.target.value)} sx={fpInputSx} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Transferencias" value={transferencias} onChange={(e) => setTransferencias(e.target.value)} sx={fpInputSx} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Otros" value={otros} onChange={(e) => setOtros(e.target.value)} sx={fpInputSx} /></Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Total Digitado" value={fmt(totalDigitado)} InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start">RD$</InputAdornment> }}
                      sx={{ ...fpInputSx, "& .MuiOutlinedInput-root": { ...fpInputSx["& .MuiOutlinedInput-root"], bgcolor: FP.color.surfaceAlt, fontWeight: 700 } }} />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={sectionBox}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <SectionTitle icon={CurrencyExchangeOutlinedIcon} color={FP.color.info}>Otras monedas</SectionTitle>
                  <Button size="small" startIcon={<AddIcon />} onClick={addMoneda} sx={btnOutline(FP.color.info)}>Agregar moneda</Button>
                </Stack>
                {monedas.length === 0 ? (
                  <Typography sx={{ fontSize: "0.84rem", color: FP.color.textMuted, py: 1 }}>
                    Sin monedas adicionales. Usa "Agregar moneda" para incluir US$, EU$, etc. con su tasa.
                  </Typography>
                ) : (
                  <>
                    <Stack direction="row" sx={{ px: 0.5, mb: 0.5 }}>
                      <Typography sx={{ flex: 1.2, fontSize: "0.68rem", fontWeight: 700, color: FP.color.textMuted, textTransform: "uppercase" }}>Moneda</Typography>
                      <Typography sx={{ flex: 1, fontSize: "0.68rem", fontWeight: 700, color: FP.color.textMuted, textTransform: "uppercase" }}>Total</Typography>
                      <Typography sx={{ flex: 1, fontSize: "0.68rem", fontWeight: 700, color: FP.color.textMuted, textTransform: "uppercase" }}>Tasa</Typography>
                      <Typography sx={{ flex: 1, textAlign: "right", fontSize: "0.68rem", fontWeight: 700, color: FP.color.textMuted, textTransform: "uppercase" }}>Equiv. RD</Typography>
                      <Box sx={{ width: 40 }} />
                    </Stack>
                    {monedas.map((m, i) => (
                      <Stack key={i} direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Box sx={{ flex: 1.2 }}><TextField fullWidth size="small" placeholder="US$" value={m.moneda} onChange={setMonedaF(i, "moneda")} sx={fpInputSx} /></Box>
                        <Box sx={{ flex: 1 }}><TextField fullWidth size="small" type="number" value={m.total} onChange={setMonedaF(i, "total")} sx={fpInputSx} /></Box>
                        <Box sx={{ flex: 1 }}><TextField fullWidth size="small" type="number" value={m.tasa} onChange={setMonedaF(i, "tasa")} sx={fpInputSx} /></Box>
                        <Typography sx={{ flex: 1, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmt(num(m.total) * num(m.tasa))}</Typography>
                        <IconButton size="small" onClick={() => removeMoneda(i)} sx={{ color: FP.color.danger }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Stack>
                    ))}
                  </>
                )}
              </Box>

              <Box sx={{ ...sectionBox, mb: 0, bgcolor: FP.color.navy, color: "#fff" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, opacity: 0.85 }}>Total Digitado (RD)</Typography>
                  <Typography sx={{ fontWeight: 700 }}>RD$ {fmt(totalDigitado)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, opacity: 0.85 }}>Otras monedas (RD)</Typography>
                  <Typography sx={{ fontWeight: 700 }}>RD$ {fmt(sumaConvertido)}</Typography>
                </Stack>
                <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.2)" }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>Total General (RD)</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>RD$ {fmt(totalGeneral)}</Typography>
                </Stack>
              </Box>

              <FormControlLabel
                sx={{ mt: 1.5 }}
                control={<Switch checked={problema} onChange={(e) => setProblema(e.target.checked)} color="error" />}
                label={<Typography sx={{ fontWeight: 700, color: problema ? FP.color.danger : FP.color.textSecondary }}>¿Problema con el cuadre de caja?</Typography>}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.2 }}>
          <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnGhost}>Limpiar</Button>
          <Button onClick={() => setCrearOpen(false)} sx={btnGhost}>Cerrar</Button>
          <Button startIcon={<SaveOutlinedIcon />} onClick={guardar} disabled={guardando} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
            {guardando ? "Grabando…" : "Grabar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Detalle (historial) */}
      <Dialog open={detOpen} onClose={() => setDetOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <VisibilityOutlinedIcon sx={{ color: FP.color.navy }} />
          {detData ? `Cuadre #${detData.nocuadre}` : "Detalle del cuadre"}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: FP.color.surface, p: { xs: 2, sm: 3 } }}>
          {detLoading || !detData ? (
            <Typography sx={{ py: 4, textAlign: "center", color: FP.color.textMuted }}>
              {detLoading ? "Cargando…" : "Sin datos"}
            </Typography>
          ) : (
            <>
              <Stack direction="row" flexWrap="wrap" alignItems="center" sx={{ mb: 2, gap: 1 }}>
                <Chip label={`Fecha: ${String(detData.fecha || "").split("-").reverse().join("/")}`} sx={{ bgcolor: FP.color.surfaceAlt, fontWeight: 600 }} />
                <Chip label={`Usuario: ${detData.usuario || "—"}`} sx={{ bgcolor: FP.color.surfaceAlt, fontWeight: 600 }} />
                <Chip label={`Turno: ${detData.turno || "—"}`} sx={{ bgcolor: FP.color.surfaceAlt, fontWeight: 600 }} />
                <Chip label={`Caja: ${detData.caja_nombre || "—"}`} sx={{ bgcolor: FP.color.surfaceAlt, fontWeight: 600 }} />
                {String(detData.problema).toUpperCase() === "S"
                  ? <StatusPill label="Problema" variant="danger" />
                  : <StatusPill label="OK" variant="success" />}
              </Stack>

              <Box sx={sectionBox}>
                <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>Desglose de efectivo</SectionTitle>
                {DENOMS.filter((d) => num(detData["c" + d]) > 0).length === 0 ? (
                  <Typography sx={{ color: FP.color.textMuted }}>Sin billetes</Typography>
                ) : (
                  DENOMS.filter((d) => num(detData["c" + d]) > 0).map((d) => (
                    <Stack key={d} direction="row" justifyContent="space-between" sx={{ py: 0.3 }}>
                      <Typography>{num(detData["c" + d])} × {d.toLocaleString()}</Typography>
                      <Typography sx={{ fontVariantNumeric: "tabular-nums" }}>{fmt(num(detData["c" + d]) * d)}</Typography>
                    </Stack>
                  ))
                )}
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontWeight: 800 }}>Efectivo</Typography>
                  <Typography sx={{ fontWeight: 800, color: FP.color.success }}>RD$ {fmt(detData.efectivo)}</Typography>
                </Stack>
              </Box>

              <Box sx={sectionBox}>
                <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.accentBlue}>Métodos de pago</SectionTitle>
                {[["Efectivo", detData.efectivo], ["Tarjetas", detData.tarjetas], ["Cheques", detData.cheques], ["Transferencias", detData.transferencias], ["Otros", detData.otros]].map(([l, v]) => (
                  <Stack key={l} direction="row" justifyContent="space-between" sx={{ py: 0.3 }}>
                    <Typography>{l}</Typography>
                    <Typography sx={{ fontVariantNumeric: "tabular-nums" }}>RD$ {fmt(v)}</Typography>
                  </Stack>
                ))}
              </Box>

              {detData.monedas && detData.monedas.length > 0 && (
                <Box sx={sectionBox}>
                  <SectionTitle icon={CurrencyExchangeOutlinedIcon} color={FP.color.info}>Otras monedas</SectionTitle>
                  {detData.monedas.map((m, i) => (
                    <Stack key={i} direction="row" justifyContent="space-between" sx={{ py: 0.3 }}>
                      <Typography>{m.moneda} — {fmt(m.total)} × {fmt(m.tasa)}</Typography>
                      <Typography sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>RD$ {fmt(m.total_convertido)}</Typography>
                    </Stack>
                  ))}
                </Box>
              )}

              <Box sx={{ ...sectionBox, mb: 0, bgcolor: FP.color.navy, color: "#fff" }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ opacity: 0.85, fontWeight: 700 }}>Total Digitado (RD)</Typography>
                  <Typography sx={{ fontWeight: 700 }}>RD$ {fmt(detData.total_digitado)}</Typography>
                </Stack>
                <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.2)" }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>Total General (RD)</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>RD$ {fmt(detData.total_general)}</Typography>
                </Stack>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<PrintOutlinedIcon />} onClick={() => imprimirDet(false)} disabled={!detData} sx={btnOutline(FP.color.navy)}>Imprimir Desglose</Button>
            <Button startIcon={<PrintOutlinedIcon />} onClick={() => imprimirDet(true)} disabled={!detData} sx={btnOutline(FP.color.navy)}>Imprimir Detalle</Button>
          </Stack>
          <Button onClick={() => setDetOpen(false)} sx={btnGhost}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
